import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  ApprovalFormData,
  ApprovalLineItem,
  Approval,
  approvalTypeOptions,
  priorityOptions,
} from "./approvalsTypes";

interface CreateApprovalProps {
  open: boolean;
  onClose: () => void;
  onSave: (formData: ApprovalFormData) => Promise<void>;
  isLoading?: boolean;
  initialApproval?: Approval | null;
}

const CreateApproval: React.FC<CreateApprovalProps> = ({
  open,
  onClose,
  onSave,
  isLoading = false,
  initialApproval = null,
}) => {
  const isEditing = !!initialApproval;

  const [formData, setFormData] = useState<ApprovalFormData>({
    type: "change_order",
    title: "",
    description: "",
    lineItems: [{ description: "", quantity: 1, unit_price: 0 }],
    timelineImpact: "",
    priority: "normal",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEditing && initialApproval) {
      setFormData({
        type: initialApproval.type,
        title: initialApproval.title,
        description: initialApproval.description || "",
        lineItems: initialApproval.pricing_breakdown || [
          { description: "", quantity: 1, unit_price: 0 },
        ],
        timelineImpact: initialApproval.timeline_impact?.toString() || "",
        priority: initialApproval.priority,
      });
    }
  }, [open, initialApproval, isEditing]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (formData.lineItems.length === 0) {
      newErrors.lineItems = "At least one line item is required";
    } else {
      const invalidItems = formData.lineItems.some(
        (item: ApprovalLineItem) =>
          !item.description.trim() || item.quantity <= 0 || item.unit_price < 0,
      );
      if (invalidItems) {
        newErrors.lineItems =
          "All line items must have description, quantity > 0, and price >= 0";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddLineItem = (): void => {
    setFormData((prev: ApprovalFormData) => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        { description: "", quantity: 1, unit_price: 0 },
      ],
    }));
  };

  const handleRemoveLineItem = (index: number): void => {
    setFormData((prev: ApprovalFormData) => ({
      ...prev,
      lineItems: prev.lineItems.filter(
        (_: ApprovalLineItem, i: number) => i !== index,
      ),
    }));
  };

  const handleLineItemChange = (
    index: number,
    field: keyof ApprovalLineItem,
    value: string | number,
  ): void => {
    setFormData((prev: ApprovalFormData) => {
      const updated = [...prev.lineItems];
      if (field === "quantity" || field === "unit_price") {
        updated[index][field] = Number(value);
      } else {
        updated[index][field] = value as string;
      }
      return { ...prev, lineItems: updated };
    });
  };

  const calculateTotal = (): number => {
    return formData.lineItems.reduce(
      (sum: number, item: ApprovalLineItem) =>
        sum + item.quantity * item.unit_price,
      0,
    );
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      await onSave(formData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      type: "change_order",
      title: "",
      description: "",
      lineItems: [{ description: "", quantity: 1, unit_price: 0 }],
      timelineImpact: "",
      priority: "normal",
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? "Edit Approval Request" : "Create Approval Request"}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          {/* Type Selection */}
          <FormControl fullWidth>
            <FormLabel>Type</FormLabel>
            <Select
              value={formData.type}
              onChange={(e) =>
                setFormData((prev: ApprovalFormData) => ({
                  ...prev,
                  type: e.target.value as "change_order" | "material_selection",
                }))
              }
              sx={{ mt: 0.5 }}
            >
              {approvalTypeOptions.map(
                (opt: { value: string; label: string }) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>

          {/* Title */}
          <TextField
            label="Title"
            fullWidth
            value={formData.title}
            onChange={(e) =>
              setFormData((prev: ApprovalFormData) => ({
                ...prev,
                title: e.target.value,
              }))
            }
            error={!!errors.title}
            helperText={errors.title}
          />

          {/* Description */}
          <TextField
            label="Description (Optional)"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData((prev: ApprovalFormData) => ({
                ...prev,
                description: e.target.value,
              }))
            }
          />

          {/* Line Items Table */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">Pricing Breakdown</Typography>
              <IconButton
                size="small"
                onClick={handleAddLineItem}
                color="primary"
              >
                <AddIcon />
              </IconButton>
            </Box>

            {errors.lineItems && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {errors.lineItems}
              </Alert>
            )}

            <Paper variant="outlined" sx={{ overflow: "auto", maxHeight: 300 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell>Description</TableCell>
                    <TableCell align="right" width={80}>
                      Qty
                    </TableCell>
                    <TableCell align="right" width={100}>
                      Price
                    </TableCell>
                    <TableCell align="right" width={100}>
                      Total
                    </TableCell>
                    <TableCell align="center" width={40}>
                      Delete
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.lineItems.map(
                    (item: ApprovalLineItem, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Item description"
                            value={item.description}
                            onChange={(e) =>
                              handleLineItemChange(
                                index,
                                "description",
                                e.target.value,
                              )
                            }
                            variant="outlined"
                            sx={{ "& input": { py: 0.5 } }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: "1", step: "1" }}
                            value={item.quantity}
                            onChange={(e) =>
                              handleLineItemChange(
                                index,
                                "quantity",
                                e.target.value,
                              )
                            }
                            variant="outlined"
                            sx={{ width: 70, "& input": { py: 0.5 } }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: "0", step: "0.01" }}
                            value={item.unit_price}
                            onChange={(e) =>
                              handleLineItemChange(
                                index,
                                "unit_price",
                                e.target.value,
                              )
                            }
                            variant="outlined"
                            sx={{ width: 90, "& input": { py: 0.5 } }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          ${(item.quantity * item.unit_price).toFixed(2)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveLineItem(index)}
                            color="error"
                            disabled={formData.lineItems.length === 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            </Paper>

            {/* Total Line */}
            <Box sx={{ textAlign: "right", mt: 1 }}>
              <Typography variant="subtitle1">
                Total: <strong>${calculateTotal().toFixed(2)}</strong>
              </Typography>
            </Box>
          </Box>

          {/* Timeline Impact */}
          <TextField
            label="Timeline Impact (days)"
            type="number"
            fullWidth
            inputProps={{ min: "0", step: "1" }}
            value={formData.timelineImpact}
            onChange={(e) =>
              setFormData((prev: ApprovalFormData) => ({
                ...prev,
                timelineImpact: e.target.value,
              }))
            }
            helperText="How many days will this add to the project?"
          />

          {/* Priority */}
          <FormControl>
            <FormLabel>Priority</FormLabel>
            <RadioGroup
              row
              value={formData.priority}
              onChange={(e) =>
                setFormData((prev: ApprovalFormData) => ({
                  ...prev,
                  priority: e.target.value as "normal" | "urgent",
                }))
              }
              sx={{ mt: 0.5 }}
            >
              {priorityOptions.map((opt: { value: string; label: string }) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio />}
                  label={opt.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isLoading}>
          {isEditing ? "Update Approval" : "Create Approval"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateApproval;
